import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { COMMON_DATETIME_FORMAT_FOR_FILE_NAME } from 'constants/date';
import {
  FILE_NAME_CONTENT_DISPOSITION_PREFIX_REG_EX,
  FILE_NAME_CONTENT_DISPOSITION_REG_EX,
  IMAGE_FILE_MIME_TYPES,
  MAX_UPLOAD_COMPRESSED_FILE_SIZE,
  MAX_UPLOAD_COMPRESSED_FILE_SIZE_MB,
  OPENABLE_MIME_TYPES,
} from 'constants/file';

const PERCENTAGE = 100;
const IMAGE_MEASURE_TIMEOUT_MS = 10000;
const OBJECT_URL_REVOKE_DELAY_MS = 60000;

export const compressFile = async file => {
  const isImageNeedToBeCompressed =
    IMAGE_FILE_MIME_TYPES.includes(file.type) && file.size > MAX_UPLOAD_COMPRESSED_FILE_SIZE;

  if (isImageNeedToBeCompressed) {
    const { default: compressImage } = await import('browser-image-compression');

    return compressImage(file, {
      maxSizeMB: MAX_UPLOAD_COMPRESSED_FILE_SIZE_MB,
      useWebWorker: true,
    });
  }

  return file;
};

export const getFileDateTimeName = ({ fileName, date, extension }) =>
  `${fileName}_${dayjs(date).format(COMMON_DATETIME_FORMAT_FOR_FILE_NAME)}.${extension}`;

export const getFileNameFromContentDispositionHeader = ({ contentDisposition, extension }) => {
  // The header is often absent (e.g. not exposed on a cross-origin response), so guard
  // rather than throw on `null.match` — the caller falls back to a generic name.
  const headerMatch = contentDisposition?.match(FILE_NAME_CONTENT_DISPOSITION_REG_EX);

  if (!headerMatch) {
    return null;
  }

  const [match, encodedMatch] = headerMatch;
  const fileName = encodedMatch ? decodeURIComponent(encodedMatch) : match;

  return fileName.replace(
    new RegExp(`^${FILE_NAME_CONTENT_DISPOSITION_PREFIX_REG_EX.source}|.${extension}$`, 'gi'),
    '',
  );
};

export const openFileFromBlob = async (blob, fileName = 'document.pdf') => {
  const { getIsNativePlatform } = await import('utils/platform');

  if (getIsNativePlatform()) {
    const { downloadOrShareBlob } = await import('utils/nativeFile');

    return downloadOrShareBlob(blob, fileName);
  }

  const { openURLInNewTab } = await import('utils/navigation');

  // An object URL inherits this origin, so anything not on the render allowlist is
  // retyped and downloaded rather than run. blob.type carries the full media type,
  // parameters included, so compare the essence only.
  const mimeType = blob.type.split(';')[0].trim().toLowerCase();
  const safeBlob = OPENABLE_MIME_TYPES.includes(mimeType)
    ? blob
    : new Blob([blob], { type: 'application/octet-stream' });
  const objectUrl = URL.createObjectURL(safeBlob);
  const tab = openURLInNewTab(objectUrl);

  // The opened tab reads the object URL asynchronously, so revoking straight away
  // races it and yields a blank viewer.
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);

  return tab;
};

export const blobToBase64 = blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      resolve(reader.result);
    };

    reader.onerror = () => reject(reader.error);
  });
};

export const printFileFromBlob = async blob => {
  const { getIsNativePlatform } = await import('utils/platform');

  if (getIsNativePlatform()) {
    const { downloadOrShareBlob } = await import('utils/nativeFile');

    return downloadOrShareBlob(blob, 'document.pdf');
  }

  const dataUrl = await blobToBase64(blob);
  const { default: printFile } = await import('print-js');

  // print-js silently fails on a blob: URL — its XHR preload can miss the 200/201 status
  // check and fire the (throwing) default onError from inside the async handler, so nothing
  // prints and the caller never sees it. Hand it the raw base64 payload instead, which skips
  // that fetch entirely, and bridge print-js's onError callback into a rejected promise.
  return new Promise((resolve, reject) => {
    printFile({
      printable: dataUrl.split(',').pop(),
      type: 'pdf',
      base64: true,
      onError: reject,
      onPrintDialogClose: resolve,
    });
  });
};

// null-on-failure rather than a timeout reject: callers insert without a measured size
const measureImage = url =>
  new Promise(resolve => {
    const img = new Image();
    const timeoutId = setTimeout(() => resolve(null), IMAGE_MEASURE_TIMEOUT_MS);

    const settle = dimensions => {
      clearTimeout(timeoutId);
      resolve(dimensions);
    };

    img.onload = () => settle({ width: img.width, height: img.height });
    img.onerror = () => settle(null);
    img.src = url;
  });

export const getImageDimensions = async (url, { scale, size = 100 }) => {
  if (!url) {
    return {};
  }

  const measured = await measureImage(url);

  if (isNil(measured)) {
    return {};
  }

  return {
    width: `${(measured.width * (parseInt(size) / PERCENTAGE)) / scale}px`,
    height: `${(measured.height * (parseInt(size) / PERCENTAGE)) / scale}px`,
  };
};
